import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { Cookies } from "react-cookie";
import { RootState } from "../store";
import { axiosApi, fetchWithTokenRefresh, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_QAS, URL_QAS } from "@/constants/api_endpoints";
import {
	QAS_FILTER_ANSWER,
	QAS_FILTER_ANSWER_TYPE,
	QAS_FILTER_CATEGORY,
	QAS_FILTER_COMPLETENESS,
	QAS_FILTER_END_DATE,
	QAS_FILTER_QUESTION,
	QAS_FILTER_START_DATE,
} from "@/constants/cookieNames";
import {
	IGetAnswerProps,
	IGetFragmentProps,
	IQasState,
	IResponseFragmentData,
	IResponseGptList,
	IResponseRephrase,
	IRephraseProps,
	IGetReplacementlistProps,
	IReplaceInfoProps,
	IResponseReplacementList,
	IResponseReplacementInfo,
	IChatProps,
	IDialog,
} from "@/types/qas/qasTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";
import { AnswerFilterType, CompletenessFilterType } from "@/types/qas/questionTypes";
import { ICategory } from "@/types/qas/qasCategoryTypes";

const initialState: IQasState = {
	filter: {
		category: null,
		question: "",
		answer: "",
		completeness: "all",
		answerType: "all",
		startDate: "",
		endDate: "",
	},
	// поиск ответа
	answerToQuestion: {
		data: null,
		status: RequestStatus.IDLE,
	},
	// поиск фрагмента
	fragmentToQuestion: {
		data: null,
		fragmentForSelection: null,
		status: RequestStatus.IDLE,
	},
	// чат
	chat: {
		status: RequestStatus.IDLE,
		data: null,
		dialog: [],
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	// модели генерации
	gptList: {
		data: [],
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	// перефразирование
	paraphrase: {
		data: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	// векторный поиск
	replacementList: {
		docs: null,
		answers: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		requestData: {
			text: "",
			replacementText: "",
			categoryId: "",
			thresholdOfSimilarity: 0,
			answerType: "all",
			phraseReplacement: false,
			answersReplacement: false,
		},
	},
	// замена информации
	replacementInfo: {
		data: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// получение ответа на вопрос
export const getAnswer = createAsyncThunk(
	"qas/ask",
	async ({ question, categoryId, channel, caching, gpt, addAlias, modelGpt, promptTemplateId }: IGetAnswerProps, { dispatch, rejectWithValue, signal }) => {
		try {
			// const response: AxiosResponse<IAnswerData | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.ASK}`, {
			// 	params: {
			// 		text: question,
			// 		category: categoryId,
			// 		channel,
			// 		useCache: caching,
			// 		useGPT: gpt,
			// 		addAlias,
			// 		model: modelGpt,
			// 		template: promptTemplateId,
			// 		stream: "yes",
			// 	},
			// 	signal,
			// 	responseType: "stream",
			// });
			// return response.data;

			const params: Record<string, string> = { text: question, stream: "yes" /* yes/no */, format: "text" /* text/json */ };
			if (categoryId) params.category = categoryId;
			if (channel) params.channel = channel;
			if (caching) params.useCache = caching;
			if (gpt) params.useGPT = gpt;
			if (addAlias) params.addAlias = addAlias;
			if (modelGpt) params.model = modelGpt;
			if (promptTemplateId) params.template = promptTemplateId;

			const response = await fetchWithTokenRefresh(`${URL_QAS}/${ENDPOINTS_QAS.ASK}?${new URLSearchParams(params).toString()}`, {
				method: "GET",
				signal,
				// headers: {
				// Accept: "text/event-stream",
				// "Transfer-Encoding": "chunked",
				// },
			});
			const reader = response.body?.getReader();
			if (!reader) throw new Error("Read error");
			const textDecoder = new TextDecoder("utf-8");
			// let remainingData = ""; // оставшиеся данные
			// обработка ошибки
			if (!response.ok) {
				const { value } = await reader.read(); // читаем
				const chunkText = textDecoder.decode(value, { stream: true });
				let responseMessage = "";
				try {
					const data: unknown = JSON.parse(chunkText); // парсим данные
					if (typeof data === "object" && data && "message" in data && typeof data.message === "string") {
						responseMessage = data.message;
						throw new Error();
					} else throw new Error();
				} catch {
					throw new Error(responseMessage || `Request error (${response.status})`);
				}
			}

			// eslint-disable-next-line no-constant-condition
			while (true) {
				const { done, value } = await reader.read(); // читаем чанки
				if (done) break; // конец чтения - остановка цикла

				// Преобразуем двоичные данные (Uint8Array) в строку
				const chunkText = textDecoder.decode(value, { stream: true });
				// try {
				// 	const data = JSON.parse(chunkText); // парсим данные
				dispatch(addAskData(chunkText)); // добавляем в store
				// } catch {
				// 	console.error("Stream data parsing error");
				// }
				// const regExp = /\{.+?\}/g; // регулярка на данные
				// const concatChunkText = remainingData.concat(chunkText); // собираем кусок
				// const match = concatChunkText.match(regExp); // совпадения данных
				// if (match) {
				// 	const remData = concatChunkText.replace(regExp, ""); // оставшиеся данные
				// 	remainingData = remData;
				// 	// парсим данные
				// 	const parsedData: (IAnswerData | IResponse)[] = match.map((data) => {
				// 		try {
				// 			return JSON.parse(data);
				// 		} catch {
				// 			return {};
				// 		}
				// 	});
				// 	// собираем текст
				// 	const concatenatedData: IAnswerData = parsedData.reduce(
				// 		(acc, curr) => {
				// 			return { ...acc, text: acc.text.concat("text" in curr ? curr.text : "") };
				// 		},
				// 		{ error: 0, confidence: 0, text: "" }
				// 	);
				// 	dispatch(addAskData(concatenatedData)); // добавляем в store
				// } else {
				// 	remainingData = chunkText;
				// }
			}
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// получение фрагмента из документа на вопрос
export const getFragment = createAsyncThunk("qas/fragment", async ({ question, categoryId }: IGetFragmentProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseFragmentData | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.SEARCH}`, {
			params: {
				text: question,
				category: categoryId,
			},
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// чат
export const chatWithGpt = createAsyncThunk("qas/chat", async ({ text, history, modelGpt, promptTemplateId }: IChatProps, { dispatch, rejectWithValue }) => {
	try {
		// const response: AxiosResponse<IResponseChat | IResponse> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.CHAT}`, {
		// 	text,
		// 	history,
		// 	model: modelGpt,
		// 	useInternalTemplate: promptTemplateId,
		// });
		// return response.data;

		const params: Record<string, string> = { text, stream: "yes" /* yes/no */, format: "text" /* text/json */ };
		if (history) params.history = history;
		if (modelGpt) params.model = modelGpt;
		if (promptTemplateId) params.useInternalTemplate = promptTemplateId;

		const response = await fetchWithTokenRefresh(`${URL_QAS}/${ENDPOINTS_QAS.CHAT}`, {
			method: "POST",
			body: JSON.stringify(params),
			headers: {
				// Accept: "text/event-stream",
				// "Transfer-Encoding": "chunked",
				"Content-Type": "application/json",
			},
		});
		const reader = response.body?.getReader();
		if (!reader) throw new Error("Read error");
		const textDecoder = new TextDecoder("utf-8");
		// let remainingData = ""; // оставшиеся данные
		// обработка ошибки
		if (!response.ok) {
			const { value } = await reader.read(); // читаем
			const chunkText = textDecoder.decode(value, { stream: true });
			let responseMessage = "";
			try {
				const data: unknown = JSON.parse(chunkText); // парсим данные
				if (typeof data === "object" && data && "message" in data && typeof data.message === "string") {
					responseMessage = data.message;
					throw new Error();
				} else throw new Error();
			} catch {
				throw new Error(responseMessage || `Request error (${response.status})`);
			}
		}

		// eslint-disable-next-line no-constant-condition
		while (true) {
			const { done, value } = await reader.read(); // читаем чанки
			if (done) break; // конец чтения - остановка цикла

			// Преобразуем двоичные данные (Uint8Array) в строку
			const chunkText = textDecoder.decode(value, { stream: true });
			// try {
			// 	const data = JSON.parse(chunkText); // парсим данные
			dispatch(addChatData(chunkText)); // добавляем в store
			// } catch {
			// 	console.error("Stream data parsing error");
			// }
			// const regExp = /\{.+?\}/g; // регулярка на данные
			// const concatChunkText = remainingData.concat(chunkText); // собираем кусок
			// const match = concatChunkText.match(regExp); // совпадения данных
			// if (match) {
			// 	const remData = concatChunkText.replace(regExp, ""); // оставшиеся данные
			// 	remainingData = remData;
			// 	// парсим данные
			// 	const parsedData: (IAnswerData | IResponse)[] = match.map((data) => {
			// 		try {
			// 			return JSON.parse(data);
			// 		} catch {
			// 			return {};
			// 		}
			// 	});
			// 	// собираем текст
			// 	const concatenatedData: IAnswerData = parsedData.reduce(
			// 		(acc, curr) => {
			// 			return { ...acc, text: acc.text.concat("text" in curr ? curr.text : "") };
			// 		},
			// 		{ error: 0, confidence: 0, text: "" }
			// 	);
			// 	dispatch(addAskData(concatenatedData)); // добавляем в store
			// } else {
			// 	remainingData = chunkText;
			// }
		}
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// список моделей gpt
export const getGptList = createAsyncThunk("qas/gptList", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseGptList> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.GPT_LIST}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// перефразировка
export const rephrase = createAsyncThunk("qas/rephrase", async ({ text, modelGpt, promptTemplateId }: IRephraseProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseRephrase> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.PARAPHRASE}`, {
			text,
			model: modelGpt,
			template: promptTemplateId,
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// список всех фрагментов, подлежащих замене (векторный поиск)
export const getReplacementList = createAsyncThunk(
	"qas/replacementList",
	async ({ text, categoryId, threshold, answers }: IGetReplacementlistProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponseReplacementList> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.REPLACEMENT_LIST}`, {
				params: {
					text,
					category: categoryId,
					threshold,
					answers,
				},
			});
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// замена информации в переданном тексте
export const replaceInfo = createAsyncThunk("qas/replaceInfo", async ({ text, oldInfo, newInfo, modelGpt, templateId }: IReplaceInfoProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseReplacementInfo> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.REPLACE}`, {
			text,
			oldinfo: oldInfo,
			newinfo: newInfo,
			model: modelGpt,
			template: templateId,
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const qasSlice = createSlice({
	name: "qas",
	initialState,
	reducers: {
		// установка фильтров qas
		setFiltersByQas: (
			state,
			action: PayloadAction<{
				category?: ICategory | null;
				question?: string;
				answer?: string;
				answerType?: AnswerFilterType;
				completeness?: CompletenessFilterType;
				startDate?: string;
				endDate?: string;
			}>
		) => {
			if (typeof action.payload.category !== "undefined") {
				state.filter.category = action.payload.category;
				new Cookies().set(QAS_FILTER_CATEGORY, action.payload.category || "", { path: "/", maxAge: 2_592_000 });
			}
			if (typeof action.payload.question === "string") {
				state.filter.question = action.payload.question;
				new Cookies().set(QAS_FILTER_QUESTION, action.payload.question, { path: "/", maxAge: 2_592_000 });
			}
			if (typeof action.payload.answer === "string") {
				state.filter.answer = action.payload.answer;
				new Cookies().set(QAS_FILTER_ANSWER, action.payload.answer, { path: "/", maxAge: 2_592_000 });
			}
			if (action.payload.answerType) {
				state.filter.answerType = action.payload.answerType;
				new Cookies().set(QAS_FILTER_ANSWER_TYPE, action.payload.answerType, { path: "/", maxAge: 2_592_000 });
			}
			if (action.payload.completeness) {
				state.filter.completeness = action.payload.completeness;
				new Cookies().set(QAS_FILTER_COMPLETENESS, action.payload.completeness, { path: "/", maxAge: 2_592_000 });
			}
			if (typeof action.payload.startDate === "string") {
				state.filter.startDate = action.payload.startDate;
				new Cookies().set(QAS_FILTER_START_DATE, action.payload.startDate, { path: "/", maxAge: 2_592_000 });
			}
			if (typeof action.payload.endDate === "string") {
				state.filter.endDate = action.payload.endDate;
				new Cookies().set(QAS_FILTER_END_DATE, action.payload.endDate, { path: "/", maxAge: 2_592_000 });
			}
		},
		// добавление фрагмента для выделения в документе
		addFragmentForSelection: (state, action: PayloadAction<string>) => {
			state.fragmentToQuestion.fragmentForSelection = action.payload;
		},
		// изменение перефразированного текста
		changingParaphrasedText: (state, action: PayloadAction<{ text: string; idx: number }>) => {
			if (state.paraphrase.data) state.paraphrase.data[action.payload.idx] = action.payload.text;
		},
		// удаление перефразированного текста
		removeParaphrasedText: (state, action: PayloadAction<number>) => {
			state.paraphrase.data?.splice(action.payload, 1);
		},
		// изменение фразы/измененной фразы в таблице векторного поиска
		changePhraseInVectorTable: (state, action: PayloadAction<{ column: "phrase" | "modifiedPhrase"; idx: number; text: string }>) => {
			if (state.replacementList.docs) {
				state.replacementList.docs[action.payload.idx][action.payload.column] = action.payload.text;
			}
		},
		// изменение ответа/измененноого ответа в таблице векторного поиска
		changeAnswerInVectorTable: (state, action: PayloadAction<{ column: "answer" | "modifiedAnswer"; idx: number; text: string }>) => {
			if (state.replacementList.answers) {
				state.replacementList.answers[action.payload.idx][action.payload.column] = action.payload.text;
			}
		},
		// удаление строк в таблице векторного поиска
		deleteRowsInVectorTable: (state, action: PayloadAction<{ table: "docs" | "answers"; indices: number[] }>) => {
			if (state.replacementList.docs && action.payload.table === "docs") {
				state.replacementList.docs = state.replacementList.docs.filter((_item, idx) => !action.payload.indices.includes(idx));
			} else if (state.replacementList.answers && action.payload.table === "answers") {
				state.replacementList.answers = state.replacementList.answers.filter((_item, idx) => !action.payload.indices.includes(idx));
			}
		},
		// добавление данных ответа на вопрос
		addAskData: (state, action: PayloadAction<string>) => {
			if (state.answerToQuestion.data === null) {
				state.answerToQuestion.data = [action.payload];
			} else if (Array.isArray(state.answerToQuestion.data)) {
				state.answerToQuestion.data.push(action.payload);
			}
		},
		// добавление диалога
		addDialogue: (state, action: PayloadAction<IDialog[]>) => {
			state.chat.dialog = action.payload;
		},
		// добавление реплики в диалог
		addReplicaInDialog: (state, action: PayloadAction<IDialog>) => {
			state.chat.dialog.push(action.payload);
		},
		// добавление данных ответа gpt
		addChatData: (state, action: PayloadAction<string>) => {
			if (state.chat.data === null) {
				state.chat.data = [action.payload];
				state.chat.dialog.push({ who: "responseText", message: [action.payload] });
			} else if (Array.isArray(state.chat.data)) {
				state.chat.data.push(action.payload);
				state.chat.dialog.at(-1)?.message.push(action.payload);
			}
		},
		// очистка ответа gpt
		clearChatAnswer: (state) => {
			state.chat.status = initialState.chat.status;
			state.chat.error = initialState.chat.error;
			state.chat.message = initialState.chat.message;
			state.chat.data = initialState.chat.data;
		},
		// очистка state
		clearState: (state) => {
			state.answerToQuestion = initialState.answerToQuestion;
			state.fragmentToQuestion = initialState.fragmentToQuestion;
			state.chat = initialState.chat;
			state.gptList = initialState.gptList;
			state.paraphrase = initialState.paraphrase;
			state.replacementList = initialState.replacementList;
			state.replacementInfo = initialState.replacementInfo;
		},
		// очистка данных ответа на вопрос
		clearAnswerToQuestion: (state) => {
			state.answerToQuestion = initialState.answerToQuestion;
		},
		// очистка данных фрагмента на вопрос
		clearFragmentToQuestion: (state) => {
			state.fragmentToQuestion = initialState.fragmentToQuestion;
		},
		// очистка чата
		clearChat: (state) => {
			state.chat = initialState.chat;
		},
		// очистка фрагмента для выделения в документе
		clearFragmentForSelection: (state) => {
			state.fragmentToQuestion.fragmentForSelection = initialState.fragmentToQuestion.fragmentForSelection;
		},
		// очистка списка моделей gpt
		clearGptList: (state) => {
			state.gptList = initialState.gptList;
		},
		// очистка данных перефразировки
		clearParaphrase: (state) => {
			state.paraphrase = initialState.paraphrase;
		},
		// очистка векторного поиска
		clearReplacementList: (state) => {
			state.replacementList = initialState.replacementList;
		},
		// очистка данных замены инфо
		clearReplacementInfo: (state) => {
			state.replacementInfo = initialState.replacementInfo;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getAnswer.pending, (state) => {
				state.answerToQuestion.status = RequestStatus.LOADING;
			})
			.addCase(getAnswer.fulfilled, (state) => {
				// собрали данные в stream режиме
				if (state.answerToQuestion.data === null) {
					state.answerToQuestion.status = RequestStatus.FAILED;
					state.answerToQuestion.data = { error: 1, message: "title_noData" };
				} else {
					state.answerToQuestion.status = RequestStatus.IDLE;
				}
			})
			.addCase(getAnswer.rejected, (state, action: PayloadAction<unknown>) => {
				const { error, message, isAbortError } = getResponseError(action);
				if (isAbortError) {
					state.answerToQuestion.status = RequestStatus.IDLE;
				} else {
					state.answerToQuestion.status = RequestStatus.FAILED;
					state.answerToQuestion.data = { error, message };
				}
			})
			.addCase(getFragment.pending, (state) => {
				state.fragmentToQuestion.status = RequestStatus.LOADING;
			})
			.addCase(getFragment.fulfilled, (state, action) => {
				state.fragmentToQuestion.status = RequestStatus.IDLE;
				if (action.payload && typeof action.payload === "object") state.fragmentToQuestion.data = action.payload;
			})
			.addCase(getFragment.rejected, (state, action: PayloadAction<unknown>) => {
				state.fragmentToQuestion.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.fragmentToQuestion.data = { error, message };
			})
			.addCase(chatWithGpt.pending, (state) => {
				state.chat.status = RequestStatus.LOADING;
			})
			.addCase(chatWithGpt.fulfilled, (state) => {
				// собрали данные в stream режиме
				if (state.chat.data === null) {
					state.chat.status = RequestStatus.FAILED;
					state.chat.error = 1;
					state.chat.message = "title_noData";
				} else {
					state.chat.status = RequestStatus.IDLE;
				}
			})
			.addCase(chatWithGpt.rejected, (state, action: PayloadAction<unknown>) => {
				const { error, message, isAbortError } = getResponseError(action);
				if (isAbortError) {
					state.chat.status = RequestStatus.IDLE;
				} else {
					state.chat.status = RequestStatus.FAILED;
					state.chat.error = error;
					state.chat.message = message;
				}
			})
			.addCase(getGptList.pending, (state) => {
				state.gptList.status = RequestStatus.LOADING;
			})
			.addCase(getGptList.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "models" in action.payload && Array.isArray(action.payload.models)) {
					state.gptList.status = RequestStatus.IDLE;
					state.gptList.data = action.payload.models.sort();
				} else state.gptList.status = RequestStatus.FAILED;
			})
			.addCase(getGptList.rejected, (state, action: PayloadAction<unknown>) => {
				state.gptList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.gptList.error = error;
				state.gptList.message = message;
			})
			.addCase(rephrase.pending, (state) => {
				state.paraphrase.status = RequestStatus.LOADING;
			})
			.addCase(rephrase.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "texts" in action.payload && action.payload.texts) {
					state.paraphrase.status = RequestStatus.IDLE;
					state.paraphrase.data = action.payload.texts;
				} else state.paraphrase.status = RequestStatus.FAILED;
			})
			.addCase(rephrase.rejected, (state, action: PayloadAction<unknown>) => {
				state.paraphrase.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.paraphrase.error = error;
				state.paraphrase.message = message;
			})
			.addCase(getReplacementList.pending, (state, action) => {
				state.replacementList.status = RequestStatus.LOADING;
				state.replacementList.requestData.text = action.meta.arg.text;
				state.replacementList.requestData.categoryId = action.meta.arg.categoryId;
				state.replacementList.requestData.thresholdOfSimilarity = action.meta.arg.threshold || 0.6;
				state.replacementList.requestData.answerType = action.meta.arg.answers;
			})
			.addCase(getReplacementList.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && Array.isArray(action.payload.docs) && Array.isArray(action.payload.answers)) {
					state.replacementList.status = RequestStatus.IDLE;
					state.replacementList.docs = action.payload.docs.map((item) => ({ ...item, modifiedPhrase: "" }));
					state.replacementList.answers = action.payload.answers.map((item) => ({ ...item, modifiedAnswer: "" }));
					state.replacementList.requestData.phraseReplacement = false;
					state.replacementList.requestData.answersReplacement = false;
				} else state.replacementList.status = RequestStatus.FAILED;
			})
			.addCase(getReplacementList.rejected, (state, action: PayloadAction<unknown>) => {
				state.replacementList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.replacementList.error = error;
				state.replacementList.message = message;
			})
			.addCase(replaceInfo.pending, (state, action) => {
				state.replacementInfo.status = RequestStatus.LOADING;

				state.replacementList.requestData.replacementText = action.meta.arg.newInfo;

				if (action.meta.arg.table === "docs" && Array.isArray(state.replacementList.docs)) {
					state.replacementList.docs[action.meta.arg.index].modifiedPhrase = "title_processing";
					state.replacementList.requestData.phraseReplacement = true;
				} else if (action.meta.arg.table === "answers" && Array.isArray(state.replacementList.answers)) {
					state.replacementList.answers[action.meta.arg.index].modifiedAnswer = "title_processing";
					state.replacementList.requestData.answersReplacement = true;
				}
			})
			.addCase(replaceInfo.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "text" in action.payload && typeof action.payload.text === "string") {
					state.replacementInfo.status = RequestStatus.IDLE;
					state.replacementInfo.data = action.payload.text;
					if (action.meta.arg.table === "docs" && Array.isArray(state.replacementList.docs)) {
						state.replacementList.docs[action.meta.arg.index].modifiedPhrase = action.payload.text;
					} else if (action.meta.arg.table === "answers" && Array.isArray(state.replacementList.answers)) {
						state.replacementList.answers[action.meta.arg.index].modifiedAnswer = action.payload.text;
					}
				} else state.replacementInfo.status = RequestStatus.FAILED;
			})
			.addCase(replaceInfo.rejected, (state, action) => {
				state.replacementInfo.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.replacementInfo.error = error;
				state.replacementInfo.message = message;
				if (action.meta.arg.table === "docs" && Array.isArray(state.replacementList.docs)) {
					state.replacementList.docs[action.meta.arg.index].modifiedPhrase = message || "<error>";
				} else if (action.meta.arg.table === "answers" && Array.isArray(state.replacementList.answers)) {
					state.replacementList.answers[action.meta.arg.index].modifiedAnswer = message || "<error>";
				}
			});
	},
});

export const {
	setFiltersByQas,
	addFragmentForSelection,
	changingParaphrasedText,
	removeParaphrasedText,
	changePhraseInVectorTable,
	changeAnswerInVectorTable,
	deleteRowsInVectorTable,
	addAskData,
	addDialogue,
	addReplicaInDialog,
	addChatData,
	clearChatAnswer,
	clearState,
	clearAnswerToQuestion,
	clearFragmentToQuestion,
	clearChat,
	clearFragmentForSelection,
	clearGptList,
	clearParaphrase,
	clearReplacementList,
	clearReplacementInfo,
} = qasSlice.actions;

export const selectFilter = (state: RootState) => state.qas.filter;

export const selectAnswerToQuestion = (state: RootState) => state.qas.answerToQuestion;
export const selectFragmentToQuestion = (state: RootState) => state.qas.fragmentToQuestion;
export const selectChat = (state: RootState) => state.qas.chat;

export const selectGptList = (state: RootState) => state.qas.gptList;
export const selectParaphrase = (state: RootState) => state.qas.paraphrase;
export const selectReplacementList = (state: RootState) => state.qas.replacementList;
export const selectReplacementInfo = (state: RootState) => state.qas.replacementInfo;

export default qasSlice.reducer;
